import { BOOK_PAGE_LD } from "@/lib/structured-data/entity-graph";
import { btn } from "@/components/ds/styles";
import { canonicalLink, canonicalMeta } from "@/lib/seo";
import { problemLabels } from "@/lib/intake-diag";
import { OrderTermsNote } from "@/components/payments/order-terms-note";
import { formatUsdShort } from "@/lib/payments/currency";
import { createFileRoute, Link, useHydrated, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Banknote, Eye, Loader2, Lock, MapPin, MessageCircle, SkipForward, Video, WandSparkles } from "lucide-react";
import { StepIndicator } from "@/components/ds/step-indicator";
import { OptionCard } from "@/components/ds/option-card";
import { TabBar } from "@/components/ds/tab-bar";
import { Field, DS_INPUT, DS_INPUT_INVALID } from "@/components/ds/field";
import { Progress } from "@/components/ui/progress";
import { HelpCard } from "@/components/booking/help-card";
import { useHeaderEndSlot } from "@/components/public/header-slot";
import { BOOKING_STEPS, displayStepFor } from "@/lib/booking-steps";
import { formatSypPrice } from "@/lib/payments/syp";

import {
  listServices,
  getIntakeForm,
  getPublicSiteSettings,
  listPaymentInstructions,
} from "@/lib/public.functions";
import { createBooking, submitBookingReceipt } from "@/lib/booking.functions";
import { listActiveProblems } from "@/lib/problem-catalog.functions";
import {
  applyAutoFill,
  estimateMinutes,
  fillSections,
  missingRequiredIds,
} from "@/lib/intake-logic";
import { IntakeSectionBody, scrollToItem } from "@/components/intake/intake-question";
import { ensureAnonSession } from "@/lib/auth/anon-session";
import { supabase } from "@/integrations/supabase/client";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { PhoneField, useDial, toE164 } from "@/components/common/phone-field";
import { arCount } from "@/lib/ar-count";
import { buildBookingConfirmMessage } from "@/lib/booking-whatsapp";
import { getMyRole } from "@/lib/dr-sara.functions";
import {
  bookingCompletionForMode,
  buildSyntheticIntakeAnswers,
  canUseBookingPreview,
} from "@/lib/booking-preview";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { Button } from "@/components/ui/button";
import {
  bookingPaymentRegionFor,
  defaultMethodFor,
  isMethodAllowed,
  paymentMethodForRegion,
  paymentMethodsFor,
  type SessionMode,
} from "@/lib/booking-payment";
import { BookingPaymentPanel, type BookingPaymentRegion } from "@/components/payments/booking-payment-panel";

const BOOK_TITLE = "حجز استشارة مع د. سارة النحّاس — 45 دقيقة أونلاين أو بالعيادة بدمشق";
const BOOK_DESCRIPTION =
  "استشارة تربوية فردية 45 دقيقة مع د. سارة النحّاس، أونلاين من أي بلد أو بالعيادة في أبو رمانة بدمشق. 75$ للجلسة أو 4 جلسات بـ 250$. راسلونا على واتساب أولاً لنتفق على الموعد.";

const searchSchema = z.object({
  service: z.string().optional(),
  attempt_id: z.string().uuid().optional(),
});

const PACKAGE_SLUG = "monthly-package-4";

const contactSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, "الاسم قصير — بدنا الاسم الكامل.")
    .max(100, "الاسم أطول من اللازم."),
  phone: z.string().min(8, "ما وصلنا الرقم كامل — ناقصه رمز البلد."),
  country: z.string().trim().length(2, "البلد مطلوب."),
  city: z.string().trim().max(80, "اسم المدينة طويل جدًا.").optional().or(z.literal("")),
});
type ContactErrors = Partial<Record<"fullName" | "phone" | "country" | "city", string>>;

function validateContact(input: {
  fullName: string;
  phone: string;
  country: string;
  city: string;
}): ContactErrors {
  const res = contactSchema.safeParse(input);
  if (res.success) return {};
  const errs: ContactErrors = {};
  for (const issue of res.error.issues) {
    const key = issue.path[0] as keyof ContactErrors | undefined;
    if (key && !errs[key]) errs[key] = issue.message;
  }
  return errs;
}

const servicesQ = () => queryOptions({ queryKey: ["public", "services"], queryFn: () => listServices() });
const formQ = () => queryOptions({ queryKey: ["public", "intake_form"], queryFn: () => getIntakeForm() });
const settingsQ = () =>
  queryOptions({ queryKey: ["public", "site_settings"], queryFn: () => getPublicSiteSettings() });
const payQ = () =>
  queryOptions({ queryKey: ["public", "payment_instructions"], queryFn: () => listPaymentInstructions() });
const problemsQ = () =>
  queryOptions({ queryKey: ["public", "problem_catalog"], queryFn: () => listActiveProblems() });

export const Route = createFileRoute("/_public/book/")({
  ssr: false,
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: BOOK_TITLE },
      { name: "description", content: BOOK_DESCRIPTION },
      { property: "og:title", content: BOOK_TITLE },
      { property: "og:description", content: BOOK_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { name: "twitter:title", content: BOOK_TITLE },
      { name: "twitter:description", content: BOOK_DESCRIPTION },
      ...canonicalMeta("/book"),
    ],
    links: [canonicalLink("/book")],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(BOOK_PAGE_LD) }],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(servicesQ()),
      context.queryClient.ensureQueryData(formQ()),
      context.queryClient.ensureQueryData(settingsQ()),
      context.queryClient.ensureQueryData(problemsQ()),
      context.queryClient.ensureQueryData(payQ()),
    ]);
  },
  notFoundComponent: RouteNotFound,
  errorComponent: RouteError,
  component: BookPage,
});

// ssr:false → the server sends an empty shell. Render that same empty shell on
// the first browser render, then mount the wizard, so hydration matches (#418).
function BookPage() {
  const hydrated = useHydrated();
  return hydrated ? <BookWizard /> : <div className="min-h-[80svh]" />;
}

type WizardState = {
  serviceSlug: string | null;
  sessionMode: SessionMode;
  fullName: string;
  phoneCountry: string;
  phoneNational: string;
  country: string;
  city: string;
  intakeAnswers: Record<string, any>;
  intakeSection: number;
  paymentMethod: string;
  consentChildData: boolean;
};

// v4 — no time slot, split phone, and the reworked case form (auto-filled items).
const STORAGE_KEY = "dr-sara.book.wizard.v4";

function blank(): WizardState {
  return {
    serviceSlug: null,
    sessionMode: "online",
    fullName: "",
    phoneCountry: "SY",
    phoneNational: "",
    country: "",
    city: "",
    intakeAnswers: {},
    intakeSection: 0,
    paymentMethod: "",
    consentChildData: false,
  };
}

function loadState(): WizardState {
  if (typeof window === "undefined") return blank();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? { ...blank(), ...JSON.parse(raw) } : blank();
  } catch {
    return blank();
  }
}

function scrollTop() {
  try {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch {}
}

function BookWizard() {
  const navigate = useNavigate();
  const { data: services } = useSuspenseQuery(servicesQ());
  const { data: intakeForm } = useSuspenseQuery(formQ());
  const { data: settings } = useSuspenseQuery(settingsQ());
  useSuspenseQuery(problemsQ());
  const { data: payInstructions } = useSuspenseQuery(payQ());
  const createFn = useServerFn(createBooking);
  const uploadReceipt = useServerFn(submitBookingReceipt);

  const [state, setState] = useState<WizardState>(() => blank());
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [stateReady, setStateReady] = useState(false);
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [startingSession, setStartingSession] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);
  const [receiptDone, setReceiptDone] = useState(false);
  const [paymentRegion, setPaymentRegion] = useState<BookingPaymentRegion>("SY");
  const didRestoreState = useRef(false);
  const submitInFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setHasSession(Boolean(data.session));
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => setHasSession(Boolean(session)));
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const { data: viewerRole } = useQuery({
    queryKey: ["me", "role"],
    queryFn: () => getMyRole(),
    enabled: hasSession,
    staleTime: 60_000,
    retry: false,
  });
  const canPreview = canUseBookingPreview(viewerRole);

  const clinicAddress = settings.clinic_address_ar || "أبو رمانة، دمشق";
  const clinicWhatsapp = settings.clinic_whatsapp;

  const singleService = useMemo(
    () => services.find((s: any) => s.slug !== PACKAGE_SLUG) ?? null,
    [services],
  );
  const packageService = useMemo(
    () => services.find((s: any) => s.slug === PACKAGE_SLUG) ?? null,
    [services],
  );

  const dial = useDial(state.phoneCountry);
  const phoneE164 = toE164(dial, state.phoneNational);

  useEffect(() => {
    if (didRestoreState.current) return;
    didRestoreState.current = true;
    setState(loadState());
    setStateReady(true);
  }, []);

  // Default the type to the single consultation.
  useEffect(() => {
    if (!stateReady) return;
    if (state.serviceSlug) return;
    if (singleService) setState((s) => ({ ...s, serviceSlug: singleService.slug }));
  }, [stateReady, state.serviceSlug, singleService]);

  useEffect(() => {
    if (!stateReady) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state, stateReady]);

  // Keep the phone dial country aligned with the residence country when the
  // client hasn't typed a number yet.
  useEffect(() => {
    if (!stateReady || state.phoneNational || !state.country) return;
    setState((s) => (s.phoneCountry === s.country ? s : { ...s, phoneCountry: s.country }));
  }, [state.country, state.phoneNational, stateReady]);

  useEffect(() => {
    if (!stateReady || !state.country) return;
    const input = { country: state.country, sessionMode: state.sessionMode };
    if (state.paymentMethod && isMethodAllowed(input, state.paymentMethod)) return;
    const next = defaultMethodFor(input);
    if (next !== state.paymentMethod) setState((current) => ({ ...current, paymentMethod: next }));
  }, [state.country, state.sessionMode, state.paymentMethod, stateReady]);

  useEffect(() => {
    scrollTop();
  }, [step]);

  const service = useMemo(
    () => services.find((s: any) => s.slug === state.serviceSlug) ?? singleService,
    [services, state.serviceSlug, singleService],
  );
  const paymentOptions = paymentMethodsFor({
    country: state.country,
    sessionMode: state.sessionMode,
  });
  const selectedMethod = paymentOptions.find((m) => m.value === state.paymentMethod) ?? null;


  const intakeSchema: any = (intakeForm as any)?.schema ?? {};
  const intakeSections: any[] = intakeSchema.sections ?? [];
  const visibleSections = useMemo(
    () => fillSections(intakeSchema, state.intakeAnswers),
    [intakeSchema, state.intakeAnswers],
  );
  const intakeTotal = visibleSections.length;
  const clampedIntakeSection = intakeTotal === 0 ? 0 : Math.min(state.intakeSection, intakeTotal - 1);
  const currentIntakeSection = visibleSections[clampedIntakeSection] ?? null;
  const estimate = useMemo(
    () => estimateMinutes(visibleSections, state.intakeAnswers),
    [visibleSections, state.intakeAnswers],
  );
  const [intakeErrorIds, setIntakeErrorIds] = useState<string[]>([]);

  // Hidden questions store their not-applicable answer so the record stays complete.
  useEffect(() => {
    if (!stateReady || intakeSections.length === 0) return;
    const filled = applyAutoFill(intakeSections, state.intakeAnswers);
    if (filled !== state.intakeAnswers) setState((s) => ({ ...s, intakeAnswers: filled }));
  }, [stateReady, intakeSections, state.intakeAnswers]);

  useEffect(() => {
    if (state.intakeSection > clampedIntakeSection) {
      setState((s) => ({ ...s, intakeSection: clampedIntakeSection }));
    }
  }, [clampedIntakeSection, state.intakeSection]);

  const missingIntake = useMemo(
    () => missingRequiredIds(currentIntakeSection, state.intakeAnswers),
    [currentIntakeSection, state.intakeAnswers],
  );
  const canProceed1 = !!service;

  function focusFirstError() {
    try {
      const el = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus?.();
    } catch {}
  }

  async function next() {
    if (step === 1 && !canProceed1) {
      toast.error("نوع الجلسة مطلوب.");
      return;
    }
    if (step === 2) {
      const errs = validateContact({
        fullName: state.fullName,
        phone: phoneE164,
        country: state.country,
        city: state.city,
      });
      setContactErrors(errs);
      if (Object.keys(errs).length > 0) {
        toast.error("في حقول ناقصة — ملوّنينها فوق.");
        setTimeout(focusFirstError, 50);
        return;
      }
      setStartingSession(true);
      try {
        await ensureAnonSession();
      } catch (e) {
        console.error("ensureAnonSession failed", e);
        toast.error("ما زبط بدء الطلب، منجرّب كمان مرة؟");
        return;
      } finally {
        setStartingSession(false);
      }
    }
    if (step === 3) {
      if (missingIntake.length > 0) {
        setIntakeErrorIds(missingIntake);
        toast.error("في حقول ناقصة — ملوّنينها فوق.");
        setTimeout(() => scrollToItem(missingIntake[0]), 60);
        return;
      }
      setIntakeErrorIds([]);
      if (intakeTotal > 0 && clampedIntakeSection < intakeTotal - 1) {
        setState((s) => ({ ...s, intakeSection: clampedIntakeSection + 1 }));
        scrollTop();
        return;
      }
    }
    setStep((s) => Math.min(4, s + 1));
  }

  function back() {
    // The payment step comes after the request is saved — no going back to edit it.
    if (step === 5) return;
    if (step === 3 && clampedIntakeSection > 0) {
      setState((s) => ({ ...s, intakeSection: clampedIntakeSection - 1 }));
      scrollTop();
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  }


  function requestMessage(bookingRef: string) {
    return buildBookingConfirmMessage({
      bookingRef,
      fullName: state.fullName,
      serviceTitle: service?.title_ar ?? "استشارة",
      priceUsd: Number(service?.price_usd ?? 0).toFixed(0),
      sessionMode: state.sessionMode,
      clinicAddress,
      paymentLabel: selectedMethod?.label ?? "يُحدَّد لاحقًا",
      countryLabel: COUNTRIES.find((c) => c.value === state.country)?.label ?? state.country,
      city: state.city,
    });
  }

  function prefillContactForPreview() {
    if (!canPreview) return;
    setPreviewMode(true);
    setContactErrors({});
    setState((current) => ({
      ...current,
      fullName: "معاينة إدارية",
      phoneCountry: "SY",
      phoneNational: "900000000",
      country: "SY",
      city: "دمشق",
    }));
    toast.success("تمت تعبئة بيانات تجريبية للمعاينة فقط.");
  }

  function skipContactForPreview() {
    if (!canPreview) return;
    prefillContactForPreview();
    setStep(3);
  }

  function prefillIntakeForPreview() {
    if (!canPreview) return;
    const answers = buildSyntheticIntakeAnswers(intakeSchema);
    setPreviewMode(true);
    setIntakeErrorIds([]);
    setState((current) => ({ ...current, intakeAnswers: answers, intakeSection: 0 }));
    toast.success("تمت تعبئة نموذج تجريبي للمعاينة فقط.");
  }

  function skipIntakeForPreview() {
    if (!canPreview) return;
    prefillIntakeForPreview();
    setStep(4);
  }

  function previewPaymentStep() {
    if (bookingCompletionForMode(previewMode) !== "finish_preview") return;
    setPaymentRegion(bookingPaymentRegionFor(state.paymentMethod));
    setStep(5);
  }

  function finishPreview() {
    if (bookingCompletionForMode(previewMode) !== "finish_preview") return;
    setPreviewMode(false);
    setStep(1);
    setCreatedBookingId(null);
    setReceiptDone(false);
    setState(blank());
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    toast.success("انتهت المعاينة من دون حفظ أي بيانات.");
  }

  async function submitBooking() {
    if (submitInFlight.current) return;
    if (!stateReady) {
      toast.message("نجهّز بيانات الطلب، لحظة واحدة.");
      return;
    }
    if (!service) {
      toast.error("ما في خدمة متاحة هلّق.");
      setStep(1);
      return;
    }
    const paymentMethod = state.paymentMethod;
    if (
      !paymentMethod ||
      !isMethodAllowed({ country: state.country, sessionMode: state.sessionMode }, paymentMethod)
    ) {
      toast.error("طريقة الدفع مطلوبة.");
      return;
    }
    if (!state.consentChildData) {
      toast.error("الموافقة على معالجة البيانات مطلوبة قبل تأكيد الحجز.");
      return;
    }

    // Open the tab synchronously — after an await, mobile browsers block it.
    let waWindow: Window | null = null;
    try {
      waWindow = window.open("", "_blank");
    } catch {
      waWindow = null;
    }

    submitInFlight.current = true;
    setSubmitting(true);
    try {
      await ensureAnonSession();
      const res = await createFn({
        data: {
          serviceId: service.id,
          fullName: state.fullName,
          phone: phoneE164,
          country: state.country,
          city: state.city,
          preferredTimes: [],
          intakeAnswers: state.intakeAnswers,
          paymentMethod,
          sessionMode: state.sessionMode,
          consentChildData: state.consentChildData,
        },
      });
      sessionStorage.removeItem(STORAGE_KEY);
      const href = toWhatsAppLink(clinicWhatsapp, requestMessage(res.id.slice(0, 8)));
      if (waWindow && href) {
        waWindow.location.href = href;
      } else {
        waWindow?.close();
      }
      toast.success("وصلنا طلبك — رسالة واتساب جاهزة لتثبيت الموعد.");
      setCreatedBookingId(res.id);
      // Always move to the booking's own URL: it shows the same payment panel
      // and survives refreshes / returns from WhatsApp or PayPal.
      await navigate({ to: "/book/confirm/$id", params: { id: res.id }, replace: true });
    } catch (e: any) {
      console.error("Booking request failed", e);
      waWindow?.close();
      toast.error("ما زبط إرسال الطلب. البيانات محفوظة — منجرّب كمان مرة؟");
      submitInFlight.current = false;
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadBookingReceipt(file: File) {
    if (!createdBookingId) return;
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) throw new Error("انتهت الجلسة — تسجيل الدخول من جديد مطلوب.");
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${uid}/${createdBookingId}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("payment-receipts")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      await uploadReceipt({ data: { id: createdBookingId, receiptPath: path, paymentMethod: paymentMethodForRegion(paymentRegion) as "shamcash" | "paypal" } });
      setReceiptDone(true);
      toast.success("وصلنا الإيصال.");
    } catch (e: any) {
      console.error("Receipt upload failed", e);
      toast.error("ما انرفع الإيصال — منجرّب كمان مرة، أو بتبعتوه على واتساب.");
    }
  }

  const isClinic = state.sessionMode === "in_person";
  const isPackage = service?.slug === PACKAGE_SLUG;
  const priceUsd = Number(service?.price_usd ?? 0);
  const durationMin = Number(singleService?.duration_min ?? service?.duration_min ?? 0);
  const selectionLine = `${isClinic ? "استشارة بالعيادة" : "استشارة أونلاين"} · ${isPackage ? "4 جلسات" : "جلسة واحدة"}`;
  const countryLabel = COUNTRIES.find((c) => c.value === state.country)?.label ?? "";
  const countryRegion: BookingPaymentRegion = state.country === "SY" ? "SY" : "outside";
  const [regionTab, setRegionTab] = useState<BookingPaymentRegion | null>(null);
  const shownRegion = regionTab ?? countryRegion;
  const helpWa = toWhatsAppLink(clinicWhatsapp, "مرحباً، عندي سؤال عن الدفع لحجز استشارة.");
  const isLastIntake = intakeTotal === 0 || clampedIntakeSection >= intakeTotal - 1;
  const shown = displayStepFor(step);

  useHeaderEndSlot(
    step === 5 ? undefined : (
      <button
        type="button"
        onClick={() => {
          if (step === 1) {
            if (window.history.length > 1) window.history.back();
            else void navigate({ to: "/" });
            return;
          }
          back();
        }}
        className="inline-flex min-h-12 items-center px-2 font-display text-[15px] font-bold text-brown-500"
      >
        رجوع →
      </button>
    ),
    `book-${step}-${clampedIntakeSection}`,
  );

  const primaryBtn =
    `${btn.primary} min-h-14 w-full disabled:opacity-50`;

  return (
    <div className="mx-auto max-w-[44rem] px-5 py-8 md:py-12">
      <StepIndicator steps={BOOKING_STEPS} current={shown} />

      <div className="mt-6">
        {step === 1 && (
          <div>
            <h2 className="font-display text-[34px] font-extrabold leading-tight text-brown-700">حجز استشارة</h2>
            <p className="mt-3 text-[19px] leading-relaxed text-brown-500">
              {durationMin} دقيقة مع د. سارة. اليوم والساعة بنتفق عليهم على واتساب قبل الطلب — ما في جدول مواعيد هون.
            </p>

            <h2 className="mt-10 font-display text-[23px] font-extrabold text-brown-700">وين الجلسة؟</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <OptionCard
                className="min-h-[136px]"
                selected={!isClinic}
                onSelect={() => setState((v) => ({ ...v, sessionMode: "online" }))}
                icon={Video}
                title="أونلاين"
                subtitle="من أي بلد"
              />
              <OptionCard
                className="min-h-[136px]"
                selected={isClinic}
                onSelect={() => setState((v) => ({ ...v, sessionMode: "in_person" }))}
                icon={MapPin}
                title="بالعيادة"
                subtitle={clinicAddress}
              />
            </div>
            {isClinic && (
              <p className="mt-3 flex items-start gap-2 rounded-ds-lg bg-green-100 p-4 text-[15px] leading-relaxed text-green-800">
                <Banknote className="mt-0.5 h-5 w-5 flex-none" strokeWidth={1.75} aria-hidden />
                بالعيادة في كمان دفع كاش يوم الموعد — الاختيار بالخطوة 3.
              </p>
            )}

            <h2 className="mt-10 font-display text-[23px] font-extrabold text-brown-700">كم جلسة؟</h2>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {singleService && (
                <OptionCard
                  selected={state.serviceSlug === singleService.slug}
                  onSelect={() => setState((v) => ({ ...v, serviceSlug: singleService.slug }))}
                  title="جلسة واحدة"
                  subtitle={`${singleService.duration_min} دقيقة`}
                >
                  <span className="font-display text-[32px] font-extrabold leading-none text-amber-800" dir="ltr">
                    {formatUsdShort(singleService.price_usd)}
                  </span>
                </OptionCard>
              )}
              {packageService && (
                <OptionCard
                  selected={state.serviceSlug === packageService.slug}
                  onSelect={() => setState((v) => ({ ...v, serviceSlug: packageService.slug }))}
                  title="باقة شهرية — 4 جلسات"
                  subtitle={`${packageService.duration_min} دقيقة لكل جلسة`}
                >
                  <span className="font-display text-[32px] font-extrabold leading-none text-amber-800" dir="ltr">
                    {formatUsdShort(packageService.price_usd)}
                  </span>
                </OptionCard>
              )}
            </div>

            <div className="-mx-2 mt-8 rounded-ds-xl bg-cream-100 p-5 md:mx-0 md:p-6">
              <p className="text-[15px] font-bold text-brown-500">{selectionLine}</p>
              <p className="mt-1 font-display text-[30px] font-extrabold leading-tight text-amber-800" dir="ltr">
                {formatUsdShort(priceUsd)}
              </p>
              <button type="button" onClick={next} className={`${primaryBtn} mt-5`}>
                المتابعة — شوية تفاصيل ←
              </button>
            </div>

            <ul className="mt-8 divide-y divide-hairline-subtle">
              {[
                { icon: MessageCircle, tone: "bg-green-100 text-green-800", text: "اليوم والساعة على واتساب قبل الطلب، بتوقيتك." },
                { icon: Lock, tone: "bg-blue-100 text-blue-700", text: "اللي بينكتب بالخطوة الجاية بيوصل لد. سارة قبل الجلسة." },
                {
                  icon: Banknote,
                  tone: "bg-amber-100 text-amber-800",
                  text: "PayPal من خارج سوريا · شام كاش أو حوالة الهرم من داخلها · كاش بالعيادة. كل المبيعات نهائية.",
                },
              ].map(({ icon: Icon, tone, text }) => (
                <li key={text} className="flex items-center gap-4 py-4">
                  <span className={`grid h-10 w-10 flex-none place-items-center rounded-ds-md ${tone}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <span className="text-[15px] leading-relaxed text-brown-700">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === 2 && (
          <div>
            {canPreview && (
              <AdminPreviewControls
                previewMode={previewMode}
                onPrefill={prefillContactForPreview}
                onSkip={skipContactForPreview}
              />
            )}
            <h2 className="font-display text-[30px] font-extrabold leading-tight text-brown-700">
              شوية تفاصيل، لتكون الجلسة على قدّ حالتكم
            </h2>
            <p className="mt-4 inline-flex flex-wrap items-center gap-2 rounded-ds-lg bg-cream-100 px-4 py-2.5 text-[14px] font-bold text-brown-700">
              {selectionLine} · <span dir="ltr">{formatUsdShort(priceUsd)}</span>
            </p>
            <div className="mt-8 space-y-5">
              <Field id="bk-name" label="الاسم" hint="أي اسم مريح لك." error={contactErrors.fullName}>
                <input
                  id="bk-name"
                  value={state.fullName}
                  autoComplete="name"
                  aria-invalid={contactErrors.fullName ? true : undefined}
                  onChange={(e) => {
                    const v = e.target.value;
                    setState((s) => ({ ...s, fullName: v }));
                    if (contactErrors.fullName) setContactErrors((x) => ({ ...x, fullName: undefined }));
                  }}
                  className={`${DS_INPUT} ${contactErrors.fullName ? DS_INPUT_INVALID : ""}`}
                />
              </Field>
              <div>
                <PhoneField
                  country={state.phoneCountry}
                  national={state.phoneNational}
                  onCountryChange={(v) => setState((s) => ({ ...s, phoneCountry: v }))}
                  onNationalChange={(v) => {
                    setState((s) => ({ ...s, phoneNational: v }));
                    if (contactErrors.phone) setContactErrors((x) => ({ ...x, phone: undefined }));
                  }}
                  required
                />
                {contactErrors.phone ? (
                  <p role="alert" className="mt-1.5 text-[13px] font-bold text-coral-700">
                    {contactErrors.phone}
                  </p>
                ) : (
                  <p className="mt-1.5 text-[13px] text-brown-500">نستخدمه لتأكيد الموعد فقط.</p>
                )}
              </div>
              <Field id="bk-country" label="بلد الإقامة" hint="لنعرض طريقة الدفع المناسبة." error={contactErrors.country}>
                <select
                  id="bk-country"
                  required
                  value={state.country}
                  aria-invalid={contactErrors.country ? true : undefined}
                  onChange={(e) => {
                    const v = e.target.value;
                    setState((s) => ({ ...s, country: v }));
                    setRegionTab(null);
                    if (contactErrors.country) setContactErrors((x) => ({ ...x, country: undefined }));
                  }}
                  className={`${DS_INPUT} ${contactErrors.country ? DS_INPUT_INVALID : ""}`}
                >
                  <option value="" disabled>
                    — اختيار البلد —
                  </option>
                  {COUNTRIES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="bk-city" label="المدينة" optional error={contactErrors.city}>
                <input
                  id="bk-city"
                  value={state.city}
                  onChange={(e) => {
                    const v = e.target.value;
                    setState((s) => ({ ...s, city: v }));
                  }}
                  className={DS_INPUT}
                />
              </Field>
            </div>
            <button type="button" onClick={next} disabled={startingSession} className={`${primaryBtn} mt-8`}>
              {startingSession && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              المتابعة ←
            </button>
          </div>
        )}

        {step === 3 && (
          <div>
            {canPreview && (
              <AdminPreviewControls
                previewMode={previewMode}
                onPrefill={prefillIntakeForPreview}
                onSkip={skipIntakeForPreview}
              />
            )}
            <IntakeStep
              visibleSections={visibleSections}
              sectionIndex={clampedIntakeSection}
              answers={state.intakeAnswers}
              onChange={(a) => setState((s) => ({ ...s, intakeAnswers: a }))}
              invalidIds={intakeErrorIds}
              estimate={estimate}
            />
            {missingIntake.length > 0 && (
              <p className="mt-6 text-[13px] font-bold text-brown-500">
                باقي {arCount(missingIntake.length, { one: "سؤال واحد", two: "سؤالين", few: "أسئلة", many: "سؤال" })} بهالقسم
              </p>
            )}
            <button type="button" onClick={next} className={`${primaryBtn} mt-4`}>
              {isLastIntake ? "المتابعة — الدفع ←" : "المتابعة ←"}
            </button>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="font-display text-[34px] font-extrabold leading-tight text-brown-700">الدفع، وبعدها الموعد</h2>
            <div className="mt-5 rounded-ds-lg bg-cream-100 p-5">
              <p className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-display text-[17px] font-bold text-brown-700">{selectionLine}</span>
                <span className="font-display text-[23px] font-extrabold text-amber-800" dir="ltr">
                  {formatUsdShort(priceUsd)}
                </span>
              </p>
              {countryLabel && <p className="mt-1 text-[14px] text-brown-500">بلد الإقامة: {countryLabel}</p>}
            </div>

            {previewMode && (
              <p className="mt-6 flex items-start gap-3 rounded-ds-lg bg-amber-100 p-4 text-[14px] text-brown-700">
                <Eye className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <span><strong>معاينة إدارية فقط.</strong> ما رح ينعمل حجز ولا تنحفظ بيانات ولا ينفتح واتساب.</span>
              </p>
            )}

            <div className="mt-6">
              <TabBar
                label="مكان الدفع"
                value={shownRegion}
                onChange={setRegionTab}
                options={[
                  { value: "SY", label: "من داخل سوريا" },
                  { value: "outside", label: "من خارج سوريا" },
                ]}
              />
            </div>

            {shownRegion !== countryRegion ? (
              <div className="mt-5 rounded-ds-lg bg-cream-100 p-4 text-[15px] leading-relaxed text-brown-700">
                طريقة الدفع بتمشي حسب بلد الإقامة.{" "}
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="min-h-11 font-bold text-amber-800 underline underline-offset-4"
                >
                  تعديل البلد
                </button>
              </div>
            ) : (
              <fieldset className="mt-5">
                <legend className="sr-only">طريقة الدفع</legend>
                <div className={`grid gap-3 ${paymentOptions.length >= 3 ? "grid-cols-3" : paymentOptions.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {paymentOptions.map((m) => (
                    <OptionCard
                      key={m.value}
                      selected={state.paymentMethod === m.value}
                      onSelect={() => setState((s) => ({ ...s, paymentMethod: m.value }))}
                      title={METHOD_TITLES[m.value]?.title ?? m.label}
                      subtitle={METHOD_TITLES[m.value]?.sub}
                    />
                  ))}
                </div>
                {isClinic && (
                  <p className="mt-3 text-[14px] text-brown-500">لأن الجلسة بالعيادة، في كمان دفع كاش يوم الموعد.</p>
                )}
              </fieldset>
            )}

            <div className="mt-6 rounded-ds-lg bg-cream-50 p-5">
              <p className="font-display text-[23px] font-extrabold text-brown-700" dir="ltr">
                {formatUsdShort(priceUsd)}
              </p>
              {state.country === "SY" && (
                <>
                  <p className="mt-1 font-display text-[19px] font-bold text-amber-800">{formatSypPrice(priceUsd)} ليرة جديدة</p>
                  <p className="mt-1 text-[14px] text-brown-500">
                    يعادل <span dir="ltr">{formatUsdShort(priceUsd)}</span> · التحويل بالليرة السورية
                  </p>
                </>
              )}
            </div>

            <label className="mt-6 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={state.consentChildData}
                onChange={(e) => setState((s) => ({ ...s, consentChildData: e.target.checked }))}
                className="mt-1 h-5 w-5 shrink-0 accent-amber-600"
              />
              <span className="text-[15px] leading-relaxed text-brown-700">
                موافقة على معالجة بيانات طفلي لأغراض الاستشارة والمتابعة.
              </span>
            </label>
            <div className="mt-4 space-y-1 text-[14px] leading-relaxed text-brown-500">
              <p>الحجز ما بينثبت إلا بعد ما يوصلنا الدفع.</p>
              <p>
                كل المبيعات نهائية.{" "}
                <Link to="/refund" className="font-bold text-amber-800 underline underline-offset-4">
                  سياسة الاسترداد
                </Link>
              </p>
              <OrderTermsNote />
            </div>

            {previewMode ? (
              <Button type="button" onClick={previewPaymentStep} className={`${primaryBtn} mt-6`}>
                <Eye className="h-4 w-4" />
                متابعة المعاينة لخطوة الدفع
              </Button>
            ) : (
              <button type="button" onClick={submitBooking} disabled={submitting} className={`${primaryBtn} mt-6`}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                تأكيد الحجز
              </button>
            )}
            <p className="mt-3 text-center text-[14px] leading-relaxed text-brown-500">
              {isClinic
                ? "بعد التأكيد، بنتواصل على واتساب لتحديد اليوم والساعة بالعيادة."
                : "بعد التأكيد، بنتواصل على واتساب لتحديد اليوم والساعة — بتوقيتك."}
            </p>

            <HelpCard title="صار شي بالدفع؟" href={helpWa} />
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="font-display text-[34px] font-extrabold leading-tight text-brown-700">الدفع، وبعدها الموعد</h2>
            {previewMode && (
              <p className="mt-5 flex items-start gap-3 rounded-ds-lg bg-amber-100 p-4 text-[14px] text-brown-700">
                <Eye className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <span><strong>معاينة إدارية فقط.</strong> ما انحفظ أي حجز وما رح ينرفع أي إيصال.</span>
              </p>
            )}
            <div className="mt-6">
              {receiptDone ? (
                <p className="rounded-ds-lg bg-green-100 p-5 font-display text-[17px] font-bold text-green-800">وصلنا الإيصال.</p>
              ) : (
                <BookingPaymentPanel
                  priceUsd={priceUsd}
                  serviceTitle={service?.title_ar ?? null}
                  region={paymentRegion}
                  onRegionChange={(region) => {
                    setPaymentRegion(region);
                    setState((s) => ({ ...s, paymentMethod: paymentMethodForRegion(region) }));
                  }}
                  instructions={payInstructions as any}
                  preview={previewMode}
                  {...(previewMode || !createdBookingId ? {} : { onUpload: uploadBookingReceipt })}
                />
              )}
            </div>
            {previewMode ? (
              <Button type="button" onClick={finishPreview} className={`${primaryBtn} mt-8`}>
                <Eye className="h-4 w-4" />
                إنهاء المعاينة دون حفظ
              </Button>
            ) : createdBookingId ? (
              <button
                type="button"
                onClick={() => navigate({ to: "/book/confirm/$id", params: { id: createdBookingId }, replace: true })}
                className={`${primaryBtn} mt-8`}
              >
                متابعة الحجز ←
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

const METHOD_TITLES: Record<string, { title: string; sub?: string }> = {
  shamcash: { title: "شام كاش", sub: "تحويل" },
  haram: { title: "حوالة الهرم", sub: "حوالة" },
  cash_at_clinic: { title: "كاش بالعيادة", sub: "يوم الموعد" },
  paypal: { title: "PayPal" },
};

function AdminPreviewControls({
  previewMode,
  onPrefill,
  onSkip,
}: {
  previewMode: boolean;
  onPrefill: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="mb-6 rounded-ds-lg bg-amber-100 p-4">
      <div className="flex items-start gap-2">
        <Eye className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" aria-hidden />
        <div>
          <p className="font-display text-sm font-bold text-foreground">أدوات معاينة المسؤول</p>
          <p className="mt-1 text-[13px] leading-relaxed text-brown-500">
            {previewMode ? "وضع المعاينة مفعّل — لن تُحفظ أي بيانات." : "بيانات وهمية لمعاينة الخطوات الجاية بدون حفظ."}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={onPrefill} className="min-h-11 rounded-full">
          <WandSparkles className="h-4 w-4" />
          تعبئة بيانات تجريبية
        </Button>
        <Button type="button" variant="ghost" onClick={onSkip} className="min-h-11 rounded-full">
          <SkipForward className="h-4 w-4" />
          تخطّي للمعاينة
        </Button>
      </div>
    </div>
  );
}

function IntakeStep({
  visibleSections,
  sectionIndex,
  answers,
  onChange,
  invalidIds,
  estimate,
}: {
  visibleSections: any[];
  sectionIndex: number;
  answers: Record<string, any>;
  onChange: (a: Record<string, any>) => void;
  invalidIds: string[];
  estimate: number;
}) {
  const total = visibleSections.length;
  const clampedIdx = Math.max(0, Math.min(sectionIndex, Math.max(0, total - 1)));
  const sec = visibleSections[clampedIdx];
  const isFirst = clampedIdx === 0;

  if (!sec) {
    return (
      <div>
        <h2 className="font-display text-[30px] font-extrabold text-brown-700">نموذج الحالة</h2>
        <p className="mt-2 text-[17px] text-brown-500">النموذج مش متاح هلّق — فيكم تكمّلوا للخطوة الجاية، وما بيضيع شي.</p>
      </div>
    );
  }

  function setAnswer(id: string, v: any) {
    onChange({ ...answers, [id]: v });
  }

  const pct = Math.round(((clampedIdx + 1) / total) * 100);
  // With several picked problems, each diag section names its own problem.
  const picked = problemLabels(answers.main_problem);
  const concern = (sec.visible_when?.values ?? []).find((v: string) => picked.includes(v)) ?? picked[0];
  const intro =
    sec.visible_when?.item_id === "main_problem" && concern
      ? `أسئلة إضافية عن: ${concern} — كلها اختيارية، جاوبوا عن اللي بتعرفوه.`
      : sec.intro_ar;

  return (
    <div>
      {isFirst && (
        <div className="mb-8 rounded-ds-xl bg-cream-100 p-5 md:p-6">
          <h2 className="font-display text-[23px] font-extrabold text-brown-700">قبل حجز الاستشارة</h2>
          <p className="mt-2 text-[17px] leading-[1.85] text-brown-700">
            رح نطلب منكم الإجابة عن مجموعة أسئلة قصيرة عن طفلكم وروتينه وبعض السلوكيات اللي بتلاحظوها.
          </p>
          <p className="mt-1 text-[17px] leading-[1.85] text-brown-700">
            إجاباتكم بتساعد د. سارة تحضّر للجلسة بشكل أدق وتكون الاستشارة مبنية على احتياجات طفلكم.
          </p>
          <p className="mt-1 text-[17px] leading-[1.85] text-brown-500">ما في إجابة صح أو غلط، وجميع المعلومات سرّية.</p>
        </div>
      )}

      <h2 className="font-display text-[23px] font-extrabold text-brown-700">{sec.title_ar}</h2>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-[14px] font-bold text-brown-500">
          القسم {clampedIdx + 1} من {total}
        </span>
        <span className="rounded-ds-pill bg-cream-100 px-3 py-1 text-[13px] font-bold text-brown-500">
          حوالي {arCount(estimate, { one: "دقيقة وحدة", two: "دقيقتين", few: "دقايق", many: "دقيقة" })}
        </span>
      </div>
      <Progress
        value={pct}
        aria-label="التقدّم بالنموذج"
        dir="rtl"
        className="mt-4 h-1.5 bg-cream-300 [&>div]:bg-green-500"
      />
      {intro && <p className="mt-5 rounded-ds-lg bg-blue-100 p-4 text-[15px] leading-relaxed text-brown-700">{intro}</p>}

      <div className="mt-7">
        <IntakeSectionBody section={sec} answers={answers} onChange={setAnswer} invalidIds={invalidIds} />
      </div>
    </div>
  );
}

const COUNTRIES = [
  { value: "SY", label: "سوريا" },
  { value: "SA", label: "السعودية" },
  { value: "AE", label: "الإمارات" },
  { value: "KW", label: "الكويت" },
  { value: "QA", label: "قطر" },
  { value: "BH", label: "البحرين" },
  { value: "OM", label: "عُمان" },
  { value: "JO", label: "الأردن" },
  { value: "LB", label: "لبنان" },
  { value: "EG", label: "مصر" },
  { value: "TR", label: "تركيا" },
  { value: "DE", label: "ألمانيا" },
  { value: "XX", label: "أخرى" },
];
